import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { BackHandler, StyleSheet, View } from 'react-native';
import { Button } from 'react-native-paper';
import { useSharedValue } from 'react-native-reanimated';
import type { ICarouselInstance } from 'react-native-reanimated-carousel';
import Carousel from 'react-native-reanimated-carousel';

import { BackCloseHeader } from '@/features/tutorial/components/BackCloseHeader';
import { PageContent } from '@/features/tutorial/components/PageContent';
import {
  ILLUSTRATION_HEIGHT,
  TourIllustration
} from '@/features/tutorial/components/TourIllustration';
import { usePages } from '@/features/tutorial/hooks/usePages';
import type { TourPage } from '@/features/tutorial/types/tutorial';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { DotPagination } from '@/shared/components/carousel/DotPagination';
import { OutlinedButton } from '@/shared/components/OutlinedButton';
import { outlinedButtonLabelStyle } from '@/styles/customStyles';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginBottom: 16,
    gap: 8
  },
  actions: {
    marginTop: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12
  }
});

type Props = {
  onClose: () => void;
  /** Called when the reader reaches the end and confirms; defaults to onClose. */
  onFinish?: () => void;
};

export const TutorialView: FC<Props> = ({ onClose, onFinish = onClose }) => {
  const theme = useAppTheme();
  // Start at 0 and only mount the Carousel once onLayout has reported the
  // actual container width. Seeding with useWindowDimensions().width caused a
  // visible flash on web, where the sheet is capped to a narrower max-width.
  const [viewWidth, setViewWidth] = useState<number>(0);
  const { t } = useTranslation(LocalizationNameSpace.TUTORIAL);
  const ref = useRef<ICarouselInstance>(null);
  const progress = useSharedValue<number>(0);
  const pages = usePages();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const switchToPage = useCallback(
    (index: number) => {
      ref.current?.scrollTo({
        count: index - progress.get(),
        animated: true
      });
    },
    [progress]
  );

  const switchToPrevPage = useCallback(
    () => switchToPage(currentPageIndex - 1),
    [switchToPage, currentPageIndex]
  );

  const switchToNextPage = useCallback(
    () => switchToPage(currentPageIndex + 1),
    [switchToPage, currentPageIndex]
  );

  const currentPage = pages[currentPageIndex];
  const isFirstPage = currentPageIndex === 0;
  const isLastPage = currentPageIndex === pages.length - 1;

  useFocusEffect(
    useCallback(() => {
      const onBackPress = () => {
        const pageIndex = progress.get();
        if (pageIndex === 0) {
          onClose();
        } else {
          switchToPrevPage();
        }
        return true;
      };
      const subscription = BackHandler.addEventListener(
        'hardwareBackPress',
        onBackPress
      );
      return () => subscription.remove();
    }, [progress, onClose, switchToPrevPage])
  );

  return (
    <>
      <BackCloseHeader
        canGoBack={!isFirstPage}
        onBack={switchToPrevPage}
        onClose={onClose}
      />
      <View style={styles.container}>
        {
          // Todo: create a carousel component which includes a view wrapper with the width calculations
        }
        <View
          onLayout={(e) =>
            // Note: we need a not zero number for the width
            setViewWidth(Math.max(1, e.nativeEvent.layout.width))
          }
        >
          {viewWidth > 0 ? (
            <>
              <Carousel<TourPage>
                ref={ref}
                data={pages}
                onProgressChange={(_: number, absoluteProgress: number) => {
                  progress.set(absoluteProgress);
                  const currentIndex =
                    Math.round(absoluteProgress) % pages.length;
                  setCurrentPageIndex(currentIndex);
                }}
                renderItem={({ item }) => <TourIllustration page={item} />}
                width={viewWidth}
                height={ILLUSTRATION_HEIGHT}
                snapEnabled
                pagingEnabled
                loop={false}
              />
              <DotPagination
                size={pages.length}
                progress={progress}
                onPress={switchToPage}
                color={theme.colors.primary}
              />
            </>
          ) : (
            // Reserve the illustration height so the surrounding layout does
            // not jump when the Carousel mounts one frame later.
            <View style={{ height: ILLUSTRATION_HEIGHT }} />
          )}
        </View>
        <PageContent
          title={currentPage.title}
          summary={currentPage.summary}
          features={currentPage.features}
          hint={currentPage.hint}
        />
        <View style={styles.actions}>
          {currentPage.actions?.map(({ label, onPress }) => (
            <OutlinedButton
              key={label}
              labelStyle={outlinedButtonLabelStyle}
              onPress={onPress}
            >
              {label}
            </OutlinedButton>
          ))}
          <Button
            mode="contained"
            testID="tour-next"
            onPress={() => {
              if (isLastPage) {
                onFinish();
              } else {
                switchToNextPage();
              }
            }}
          >
            {t(isLastPage ? 'close' : 'next')}
          </Button>
        </View>
      </View>
    </>
  );
};
